import { useCallback, useState } from 'react';

/**
 * Hook for opening a call script in a Document Picture-in-Picture window.
 * Falls back to an inline expandable panel on unsupported browsers.
 */
export function useCallScriptPiP() {
  const [fallbackScript, setFallbackScript] = useState<string | null>(null);

  const openScript = useCallback(async (script: string, contactName?: string) => {
    // Document PiP API (Chrome 116+)
    if ('documentPictureInPicture' in window) {
      try {
        const pipWindow = await (
          window as unknown as { documentPictureInPicture: { requestWindow: (opts: { width: number; height: number }) => Promise<Window> } }
        ).documentPictureInPicture.requestWindow({
          width: 380,
          height: 300,
        });

        const style = pipWindow.document.createElement('style');
        style.textContent = `
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
            padding: 16px;
            margin: 0;
            background: #f8fafc;
            color: #0f172a;
            line-height: 1.5;
          }
          .header {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 1px solid #e2e8f0;
          }
          .label {
            font-size: 10px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            color: #64748b;
          }
          .contact {
            font-size: 12px;
            font-weight: 700;
            color: #0f172a;
          }
          .script {
            font-size: 13px;
            line-height: 1.6;
            color: #1e293b;
          }
        `;
        pipWindow.document.head.appendChild(style);
        pipWindow.document.body.innerHTML = `
          <div class="header">
            <span class="label">Call Script</span>
            ${contactName ? `<span class="contact">— ${contactName}</span>` : ''}
          </div>
          <div class="script">${script.replace(/\n/g, '<br/>')}</div>
        `;
        return;
      } catch {
        // PiP failed (user denied, API error) — fall through to fallback
      }
    }

    // Fallback: set state so the component can render inline
    setFallbackScript(script);
  }, []);

  const closeFallback = useCallback(() => {
    setFallbackScript(null);
  }, []);

  return { openScript, fallbackScript, closeFallback };
}
